<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title">
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McButtonBack href="/report" :query="{ domain: domain }" />
            <ShareReport
              :report="report"
              :filter="baseFilter"
              @doShare="downloadReport"
              :dropdownItems="itemDownloadDropdown"
            />
            <DownloadReportDropdown :report="report" :items="itemDownloadDropdown" :actualTotal="actualTotal"/>
          </div>
        </template>
      </PageHeader>
    </div>
    <div class="d-flex align-items-center gap-2 me-2 absolute-right">
      <FavoriteReportButton
        v-if="hasAccess(['favorite-reports:update']) && !report.isSaved"
        :report="report"
      />
      <ModalSavedReport
        v-if="hasAccess(['saved-reports:read']) && !report.isSaved"
        :filter="filterValue"
        :detail="filterDetail"
        :report="report"
      />
      <McButton
        variant="secondary"
        :text="$t('report.title.reportParameter')"
        icon="icon-outline_filter"
        @click="showModal"
      />
    </div>
    <b-card :key="summaryKey" class="mb-2 mc-general__label" style="margin-top: 80px">
      <h5 class="mc-general__label">{{ $t('report.fields.dataSummary') }}</h5>
      <b-row>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="periodReport_label"
            :label="$t('report.object.reportPeriod')"
            label-for="reportPeriod"
          >
            <span v-if="!isBusy">
              {{ periodeLabel }}
            </span>
            <b-skeleton width="70%" v-else />
          </b-form-group>
        </b-col>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="licensePlate_label"
            :label="$t('report.fields.licensePlate')"
            label-for="licensePlate"
          >
            <div v-if="!isBusy" class="d-flex align-items-center gap-2">
              <span>{{ licensePlateLabel }}</span>
              <McBadge
                v-if="vehicleIdCount > 2"
                :text="`+${vehicleIdCount - 2}`"
                variant="success"
                size="small"
                type="pill"
              />
            </div>
            <b-skeleton width="70%" v-else />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>
    <AlertReportTooMany v-if="actualTotal > totalRows" />
    <McGrapqlTable
      :key="tableKey"
      bordered
      withCard
      searchable
      :fields="fields"
      :apiClient="apiClient"
      :hasCheckbox="false"
      :lazyLoadAddress="false"
      :singleSort="true"
      reportKey="geolocation"
      v-if="Object.keys(filters).length > 0"
    >
      <template #viewManager="{ handleSetDataFields, dataColumns, initialFields }">
        <McFilterSelect
          :defaultColumnOrder="initialFields"
          :currentColumnOrder="dataColumns"
          @setColumn="handleSetDataFields"
          @setColumnReport="handleColumnReport"
        />
      </template>
      <template #cell(transitOn)="data">
        {{ momentDateShortMonth(data.item.transitOn) }}<br />
        <span class="text-muted">{{ momentTimeSeconds(data.item.transitOn, true) }}</span>
      </template>
      <template #cell(driver)="data">
        {{ data.item.driver ? data.item.driver.fullname : '-' }}
      </template>
      <template #cell(vehicle)="data">
        {{ data.item.vehicle ? data.item.vehicle.licensePlate : '-' }}
      </template>
      <template #cell(imei)="data">
        {{ data.item.vehicle ? data.item.vehicle.imei : '-' }}
      </template>
      <template #cell(vehicleType)="data">
        {{
          data.item.vehicle
            ? data.item.vehicle.vehicleType
              ? data.item.vehicle.vehicleType.manufacturer
              : '-'
            : '-'
        }}
        /
        {{
          data.item.vehicle
            ? data.item.vehicle.vehicleType
              ? data.item.vehicle.vehicleType.model
              : '-'
            : '-'
        }}
        /
        {{
          data.item.vehicle
            ? data.item.vehicle.vehicleType
              ? data.item.vehicle.vehicleType.type
              : '-'
            : '-'
        }}
      </template>
    </McGrapqlTable>
    <ModalFilter
      v-if="isMounted"
      ref="modal-filter"
      :report="report"
      :unRedirect="true"
      @refresh-table="forceRerender"
    />
    <McReportDownloader />
    <ModalSavedReportVerification
      :key="modalVerifReport"
      :domain="domain"
      :message="message"
      :error="isError"
    />
    <ModalEmail
      v-if="actualTotal > totalRows"
      :id="idEmailModal"
      :recipients="recipientsEmail"
      @submit-emails="handleEmails"
    />
  </Layout>
</template>
<script>
import McButton from '@/components/McButton'
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButtonBack from '@/components/McButtonBack'
import McGrapqlTable from '@/components/McGrapqlTable'
import McFilterSelect from '@/components/McFilterSelect'
import McReportDownloader from '@/components/McReportDownloader'
import { Helper } from '@/mixin/helper'
import moment from 'moment'
import fields from './lib/fields/fields'
import ModalFilter from '@/router/views/report/components/ModalFilter'
import reportList from '../../lib/reportList.js'
import { GEOLOCATION_REPORT, GET_REPORT_GEOLOCATION } from '@/services/api/report/report'
import McBadge from '@/components/McBadge'
import { MixPanel } from '@/mixin/mixpanel'
import ModalSavedReport from '@/router/views/report/components/ModalSavedReport'
import { mapGetters } from 'vuex'
import ModalSavedReportVerification from '@/router/views/report/components/ModalSavedReportVerification'
import FavoriteReportButton from '@/router/views/report/components/FavoriteReportButton'
import { QueryCache } from '@/mixin/query-cache'
import responsive from '@/mixin/responsive'
import DownloadReportDropdown from '@/router/views/report/components/DownloadReportDropdown'
import ModalEmail from '@/router/views/report/components/ModalEmail'
import AlertReportTooMany from '../../components/AlertReportTooMany.vue'
import ShareReport from '../../components/ShareReport.vue'
moment.locale('id')

export default {
  page: {
    title: 'Report',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButtonBack,
    McGrapqlTable,
    McFilterSelect,
    McReportDownloader,
    McButton,
    ModalFilter,
    McBadge,
    ModalSavedReport,
    ModalSavedReportVerification,
    FavoriteReportButton,
    DownloadReportDropdown,
    ModalEmail,
    AlertReportTooMany,
    ShareReport,
  },
  mixins: [Helper, MixPanel, responsive],
  data() {
    return {
      isMounted: false,
      excelIcon: require('@/assets/images/download-format/excel.png'),
      pdfIcon: require('@/assets/images/download-format/pdf.png'),
      totalRows: 1,
      filters: {},
      tableKey: 0,
      isBusy: false,
      reportFields: fields.map((item, i) => ({ ...item, order: i + 1 })),
      reportKey: 'geolocation',
      filterDetail: {},
      summaryKey: 50,
      queryCache: new QueryCache(),
      message: '',
      isError: false,
      modalVerifReport: 100,
      emails: [],
      recipientsEmail: [],
      isSubmitEmail: false,
      idEmailModal: 'modal-email-geolocation',
      reportAfterEmail: '',
      actualTotal: 0,
      modalContactId: 'modal-contact',
    }
  },
  mounted() {
    const emailStorage = localStorage.getItem('emailReceiverReport')
    if (emailStorage) {
      this.recipientsEmail = emailStorage.split(',')
    }

    this.reportKey =
      this.$route.query.key && this.savedReport.find((item) => item.key === this.$route.query.key)
        ? this.$route.query.key
        : this.reportKey
    const { reportFilter, reportFilterValue } = this.getFilterReport(this.reportKey)
    this.$store.commit('report/updateFilter', reportFilter)
    this.$store.commit('report/updateFilterValue', reportFilterValue)
    this.filters = { ...this.$store.state.report.filterReport }
    this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
    this.isMounted = true
    this.track('View ' + this.report.event)
    if (!this.mediaQuery.matches) {
      this.$store.dispatch('layout/toggleMenu', true)
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    domain() {
      return this.report.domain
    },
    title() {
      return this.report.labelName ?? this.$t(this.report.label)
    },
    fields() {
      return this.reportFields
    },
    columns() {
      return this.reportFields
        .filter((item) => item.shown || item.shown === undefined)
        .map((field) => field.header)
    },
    baseFilter() {
      const filter = this.getBaseFilter(this.filters)
      const result = {
        ...filter,
        column: this.columns,
      }
      return result
    },
    filterReport() {
      const { valid, model } = this.generateFilter
      if (!valid) return null
      const filterBody = {
        ...model,
      }

      return filterBody
    },
    generateFilter() {
      const { valid, msg } = this.validateFilter(this.report.filter, this.filters)
      if (valid) {
        const model = this.baseFilter
        return { valid: true, model }
      } else {
        this.$mcToast.error(msg)
        return { valid: false, model: {} }
      }
    },
    apiClient() {
      return {
        get: async (filter) => {
          const filterReport = this.filterReport
          if (!filterReport) return {}
          const self = this
          self.isBusy = true
          const { orderBy, ...otherFilter } = filter
          const finalOrderBy = this.handleOrderBy(orderBy)
          const res = await this.queryCache.fetch(
            GET_REPORT_GEOLOCATION,
            {
              filter: {
                ...filterReport,
                orderBy: finalOrderBy,
                ...otherFilter,
              },
            },
            self.reportKey
          )
          const result = res.vehicleGeofenceHist.vehicleGeofenceHist
          self.totalRows = result?.pagination?.total ?? 0
          self.isBusy = false
          self.message = res.vehicleGeofenceHist.message
          self.isError = !res.vehicleGeofenceHist.isSuccessful
          self.modalVerifReport += 1
          self.actualTotal = result?.pagination?.actualTotal ?? 0
          return result
        },
      }
    },
    periodeLabel() {
      return this.filters.periode
        ? this.momentDateShortMonth(this.filters.periode[0]) +
            ' - ' +
            this.momentDateShortMonth(this.filters.periode[1])
        : '-'
    },
    
    licensePlateLabel() {
      if (this.filters.vehicleId && this.filters.vehicleId.length > 0) {
        return this.filterDetail != {} ? this.filterDetail.licensePlate : '-'
      } else {
        return '-'
      }
    },
    vehicleIdCount() {
      return this.filters.vehicleId && this.filters.vehicleId.length > 0
        ? this.filters.vehicleId.length
        : 0
    },
    savedReport() {
      return JSON.parse(localStorage.getItem('saveReports'))
    },
    report() {
      const reports = [...reportList].concat(this.savedReport)
      return reports.find((item) => item.key == this.reportKey)
    },
    filterValue() {
      return this.$store.state.report.filterValue
    },
    hasCsvScope() {
      return this.report.downloadOptions?.includes('csv') && this.hasAccess(['csv_report:read'])
    },
    itemDownloadDropdown() {
      let list = []
      if (this.getTotalDayReport(this.filters.periode) <= 7) {
        list = list.concat([
          {
            name: 'excel',
            label: this.$t('global.action.downloadExcel'),
            onClick: () => {
              this.downloadReport('xlsx')
            },
            icon: this.excelIcon,
          },
          {
            name: 'pdf',
            label: this.$t('global.action.downloadPdf'),
            onClick: () => {
              this.downloadReport('pdf')
            },
            icon: this.pdfIcon,
          },
        ])
      }
      if (this.hasCsvScope) {
        list.push({
          name: 'csv',
          label: this.$t('global.action.downloadCsv'),
          onClick: () => {
            this.downloadReport('csv')
          },
          icon: this.excelIcon,
        })
      }
      return list
    },
    hasEmail() {
      return this.emails && this.emails.length > 0
    },
  },
  methods: {
    getBaseFilter(filters) {
      if (!filters || Object.keys(filters).length <= 0) return {}
      const result = {}
      Object.keys(filters).forEach((item) => {
        if (
          filters[item] !== null &&
          ((Array.isArray(filters[item]) && filters[item].length > 0) ||
            (!Array.isArray(filters[item]) && filters[item]))
        ) {
          result[item] = filters[item]
        }
      })
      const { periode, condition, ...otherFilter } = result
      const startDate =
        periode.length > 0 ? moment(periode[0] + ' 00:00:00.000').toISOString() : null
      const endDate = periode.length > 0 ? moment(periode[1] + ' 23:59:59.000').toISOString() : null

      let isInside = undefined
      if (condition) {
        if (condition.length == 1 && condition[0] == 'in') {
          isInside = true
        } else if (condition.length == 1 && condition[0] == 'out') {
          isInside = false
        }
      }
      const model = {
        startDate,
        endDate,
        isInside,
        ...otherFilter,
      }
      return model
    },
    handleOrderBy(sorts) {
      if (sorts) {
        const orderBy = sorts.map((sort) => {
          const parsed = JSON.parse(sort)
          if (Object.keys(parsed)[0] === 'vehicle') {
            const newOrder = { licensePlate: Object.values(parsed)[0] }
            return JSON.stringify(newOrder)
          } else {
            return sort
          }
        })
        return orderBy
      } else {
        return undefined
      }
    },
    handleColumnReport(fields) {
      this.reportFields = fields
      this.tableKey += 1
      this.summaryKey += 1
    },
    forceRerender() {
      this.filters = { ...this.$store.state.report.filterReport }
      this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
      this.tableKey += 1
      this.summaryKey += 1
    },
    showModal() {
      this.$refs['modal-filter'].SetFormValues(this.filterValue)
      this.$bvModal.show(this.report.modal)
    },
    handleEmails(emails) {
      this.emails = emails
      this.recipientsEmail = emails
      this.isSubmitEmail = true
      if (emails.length > 0) {
        localStorage.setItem('emailReceiverReport', emails.join())
      }
      this.$bvModal.hide(this.idEmailModal)
      this.$mcToast.info(this.$t('report.text.popupEmailInfo'))
      this.downloadReport(this.reportAfterEmail)
    },
    showDownloader(periode, extension, result) {
      const downloadProgress = {
        status: 'I',
        url: null,
        name:
          `${this.title} ` +
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY') +
          `.${extension}`,
        periode:
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY'),
        downloadedAt: moment().format('DD MMM YYYY HH:mm:ss'),
        type: extension,
        id: result.geolocationReport.id,
      }
      if (result.isSuccessful) {
        this.$store.commit('report/doneProgress', downloadProgress)
      } else {
        downloadProgress.status = 'F'
        this.$store.commit('report/failProgress', downloadProgress)
      }
      this.$store.commit('report/showDownloadPopup', true)
    },
    downloadReport(extension, contact) {
      if (
        !this.isSubmitEmail &&
        this.actualTotal > this.totalRows &&
        (!contact || contact.length <= 0)
      ) {
        this.$bvModal.show(this.idEmailModal)
        this.reportAfterEmail = extension
        return
      }
      this.isSubmitEmail = false

      const fileType = this.trackDownloadReport(this.report, extension, contact)

      const filterReport = this.filterReport
      if (!filterReport) {
        setTimeout(() => {
          this.showModal()
        }, 1000)
        return false
      }
      const { periode } = this.filters
      let filterVar = {
        ...filterReport,
        subtitle: `Periode : ${this.momentDateLongMonth(periode[0])} - ${this.momentDateLongMonth(
          periode[1]
        )}`,
        downloadAs: fileType,
        emails: this.emails,
      }
      if (this.report.labelName) {
        filterVar.title = this.report.labelName
      }

      if (contact) {
        filterVar = {
          ...filterVar,
          isShared: true,
          contact: contact,
        }
      }

      this.$apollo
        .query({
          query: GEOLOCATION_REPORT,
          variables: {
            filter: filterVar,
          },
          fetchPolicy: 'no-cache',
        })
        .then((res) => {
          let result = res.data.geolocationReport
          if (!this.hasEmail && !contact) {
            this.showDownloader(periode, extension, result)
          } else if (contact) {
            if (result.isSuccessful) this.$mcToast.success(this.$t('report.text.sharedSuccess'))
            else this.$mcToast.error(this.$t('report.text.sharedFailed'))
          }
        })
    },
  },
}
</script>